import * as React from 'react';
import { SidebarLayoutWidget } from '../layout/SidebarLayoutWidget';
import { SmartHeaderWidget } from '../../widgets-smart/SmartHeaderWidget';
import { SidebarItem } from '../../routes';
import { styled } from '../../theme';
import { observer } from 'mobx-react';
import { LoaderWidget } from '../LoaderWidget';
import { PropsWithChildren, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getTransparentColor } from '@journeyapps-platform/ide-lib-reactor-utils';

export enum PageMode {
  SCROLL = 'scroll',
  FULLSCREEN = 'fullscreen'
}

export interface PageWidgetProps {
  title: ReactNode;
  items: SidebarItem[];
  activeItemKey?: string;
  loading?: boolean;
  locked?: boolean;
  mode?: PageMode;
}

export const PageWidget: React.FC<PropsWithChildren<PageWidgetProps>> = observer((props) => {
  const mode = props.mode || PageMode.SCROLL;

  const getSpinner = (): React.JSX.Element | null => {
    if (props.loading) {
      return <S.Loader />;
    }
    return null;
  };
  return (
    <S.Container>
      <SmartHeaderWidget>{props.title}</SmartHeaderWidget>
      <SidebarLayoutWidget locked={!!props.locked} {...props}>
        {mode === PageMode.SCROLL ? <S.PageContainerScroll>{props.children}</S.PageContainerScroll> : null}
        {mode === PageMode.FULLSCREEN ? <S.PageContainerFullscreen>{props.children}</S.PageContainerFullscreen> : null}
        {props.locked ? (
          <S.Locked>
            <S.LockerContainer>
              <S.LockedIcon icon="lock" />
              <S.LockedTitle>Organization locked</S.LockedTitle>
              <S.LockedDesc>Please contact JourneyApps for further assistance.</S.LockedDesc>
            </S.LockerContainer>
          </S.Locked>
        ) : null}
      </SidebarLayoutWidget>
      {getSpinner()}
    </S.Container>
  );
});

namespace S {
  export const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    max-height: 100%;
    max-width: 100%;
    position: fixed;
  `;

  export const Loader = styled(LoaderWidget)`
    position: fixed;
  `;

  export const PageContainerFullscreen = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    position: relative;
  `;

  export const PageContainerScroll = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: fit-content;
    margin-bottom: 100px;
    position: relative;
    padding: 20px;
  `;

  export const LockedTitle = styled.div`
    font-size: 30px;
    margin-top: 30px;
  `;

  export const LockedDesc = styled.div`
    font-size: 14px;
    opacity: 0.5;
    margin-top: 20px;
  `;

  export const LockedIcon = styled(FontAwesomeIcon)`
    font-size: 50px;
  `;

  export const LockerContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 50px;
    align-items: center;
    border-radius: 10px;
    background: ${(p) => p.theme.dialog.background};
    color: white;
  `;

  export const Locked = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${(p) => getTransparentColor(p.theme.background, 0.9)};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `;
}
